import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface UserProfileButtonProps {
  onProfileClick: () => void;
  onLogout: () => void;
}

const UserProfileButton: React.FC<UserProfileButtonProps> = ({
  onProfileClick,
  onLogout,
}) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-1.5 hover:bg-white/15 rounded-lg transition-colors"
      >
        {user.photoURL ? (
          <img
            src={user.photoURL}
            alt={user.name}
            className="w-8 h-8 rounded-full object-cover border-2 border-white"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-satsuma-400 to-satsuma-600 flex items-center justify-center text-white text-xs font-bold border-2 border-white">
            {getInitials(user.name || user.email)}
          </div>
        )}
        <ChevronDown size={14} className={`text-gray-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            className="absolute top-full right-0 mt-2 w-56 bg-white rounded-2xl border border-gray-200 shadow-xl z-50"
          >
            <div className="p-2">
              {/* User Info */}
              <div className="px-3 py-2 border-b border-gray-100 mb-2">
                <div className="font-medium text-gray-800">{user.name}</div>
                <div className="text-xs text-gray-500 truncate">{user.email}</div>
              </div>

              {/* Menu Items */}
              <button
                onClick={() => {
                  onProfileClick();
                  setIsOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors text-left"
              >
                <User size={18} />
                <span>Profile Settings</span>
              </button>
              <button
                onClick={() => {
                  onLogout();
                  setIsOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors text-left mt-1"
              >
                <LogOut size={18} />
                <span>Sign Out</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserProfileButton;

